import { put, takeLatest, call } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { DOZI_INBOX_URL, ZI_COOKIE, BE_ENDPOINT_ZI, BE_ENDPOINT_NON_ZI } from 'config.js';
import request from 'utils/request';
import {
  CHANGE_CURRENT_PROJECT_START,
  GET_TEAMS_ONLY_TOKEN,
  GET_APP_DETAILS,
  SEND_UPGRADE_MAIL,
} from './constants';
import {
  changeCurrentProjectSuccess,
  getTeamsOnlyToken,
  getAppDetails,
  sendUpgradeMail,
} from './actions';
import triggerNotification from 'utils/triggerNotification';
import convertErrorMsg from 'utils/convertErrorMsg';
import { NOTIFICATION } from 'common/constants';
import queryString from 'query-string';
import localStorageTemplates from 'common/local-storage-templates';
import routeTemplates from 'common/route-templates';
import { readCookie, readToken } from 'utils/readToken';
import { updateUserId, updateUserProperties } from 'common/amplitude';

export function* setCurrentProject({ payload, metadata }) {
  yield put(changeCurrentProjectSuccess(payload));
  if (metadata.redirect) yield put(push(metadata.url));
}

export function* getTeamsOnlyTokenStart({ metadata }) {
  const { conversationId, setAuthenticated, payload } = metadata;
  try {
    const response = yield call(request, {
      method: 'post',
      endpoint: 'app/login/msteams',
      config: {
        data: {
          ...payload,
        },
      },
    });
    const { data } = response;
    yield put(getTeamsOnlyToken.success({ data }));
    setAuthenticated(data.token);
    localStorage.setItem(localStorageTemplates.teamsOnlyToken, true);
    const searchParams = queryString.parse(window.location.search);
    const redirectUrl = searchParams.redirectUrl.replace(/:conversationId/, conversationId);
    yield put(push((redirectUrl && decodeURIComponent(redirectUrl)) || routeTemplates.home));
    yield put(getAppDetails.start());
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getTeamsOnlyToken.error(errorMessage));
  }
}

export function* getAppDetailsStart() {
  let token = '';
  const searchParams = queryString.parse(window.location.search);
  const isChatOnly = searchParams['chat_only'] && searchParams['chat_only'] === 'true';

  if (window.location.host.includes('zoominfo.com')) {
    token = readCookie(ZI_COOKIE);
    if (!token) {
      window.location.href = `${DOZI_INBOX_URL}`;
    }
    window.appDetails.isZIDashboard = true;
    window.appDetails.activeBE = BE_ENDPOINT_ZI;
  } else {
    window.appDetails.isZIDashboard = false;
    token = readToken();
    window.appDetails.activeBE = BE_ENDPOINT_NON_ZI;
    if (isChatOnly) {
      window.appDetails.isInsideMSTeams = true;
    }
  }

  try {
    if (token) {
      const response = yield call(request, {
        method: 'get',
        endpoint: 'app/details',
        config: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });
      const { data } = response;
      const { project, user, subscription } = data || {};
      const { features, ...rest } = subscription || {};
      /** Amplitude - Update the user properties */
      if (user?.email && user?.userId && project) {
        updateUserId(user.userId);
        updateUserProperties({
          emailId: user.email,
          userId: user.userId,
          ziUserId: user.ziUserId,
          ziTenantId: project.ziTenantId,
        });
      }

      window.appDetails = {
        ...window.appDetails,
        ...project,
        subscription: { ...features, ...rest },
        user,
      };
    }
    yield put(getAppDetails.success());
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getAppDetails.error(errorMessage));
  }
}

export function* sendUpgradeMailStart({ metadata }) {
  const { setPricingPopup, upgradeTo, closeAnyOpenPopup } = metadata || {};
  const token = readToken();
  let url = `app/pricing/upgrade/subscription`;
  if (upgradeTo) {
    url = url + `?upgradeTo=${upgradeTo}`;
  }
  try {
    yield call(request, {
      method: 'post',
      endpoint: url,
      config: {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      },
    });
    if (setPricingPopup) setPricingPopup(false);
    if (closeAnyOpenPopup) closeAnyOpenPopup(false);
    yield put(sendUpgradeMail.success());
    window.showEmailConfirmationPopup();
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(sendUpgradeMail.error(errorMessage));
  }
}

export default function* app() {
  yield takeLatest(CHANGE_CURRENT_PROJECT_START, setCurrentProject);
  yield takeLatest(GET_TEAMS_ONLY_TOKEN.START, getTeamsOnlyTokenStart);
  yield takeLatest(GET_APP_DETAILS.START, getAppDetailsStart);
  yield takeLatest(SEND_UPGRADE_MAIL.START, sendUpgradeMailStart);
}
